// src/services/dataPersistence/AutoSyncService.ts
import { debounce } from './utils/storageUtils';
import { DataSyncService } from './dataSync';
import { authService } from '../../../invite-system/src/services/AuthService'; // Adjust path as needed
//'../AuthService'; // Adjust path as needed


export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

export interface SyncState {
  status: SyncStatus;
  lastSyncTime: number;
  error: string | null;
}

export class AutoSyncService {
  private static instance: AutoSyncService;
  private dataSyncService: DataSyncService;
  private syncInterval: number | null = null;
  private syncIntervalMs: number = 120000; // 120 seconds by default
  private lastChangeTime: number = 0;
  private state: SyncState = {
    status: 'idle',
    lastSyncTime: 0,
    error: null
  };
  private isInitialized = false;
  private syncListeners: Array<(state: SyncState) => void> = [];
  private debouncedSync: () => void;
  
  private constructor(dataSyncService?: DataSyncService) {
    
    this.dataSyncService = dataSyncService || DataSyncService.getInstance();
    
    // Create debounced sync function - runs 2 seconds after last change
    this.debouncedSync = debounce(this.performSync.bind(this), 2000);
    
    // Listen for data change events
    window.addEventListener('tripper-data-updated', this.handleDataChange.bind(this));
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(dataSyncService?: DataSyncService): AutoSyncService {
    if (!AutoSyncService.instance) {
      AutoSyncService.instance = new AutoSyncService(dataSyncService);
    }
    return AutoSyncService.instance;
  }
  
  /**
   * Initialize auto-sync with options
   */
  public initialize(options: { syncIntervalMs?: number } = {}): void {
    if (this.isInitialized) return;
    
    if (options.syncIntervalMs) {
      this.syncIntervalMs = options.syncIntervalMs;
    }
    
    // Set up interval for regular syncing
    //this.startSyncInterval();
    
    this.isInitialized = true;
    //console.log(`[AutoSyncService] Initialized with ${this.syncIntervalMs}ms interval`);
  }
  
  /**
   * Start the sync interval
   */
  private startSyncInterval(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    this.syncInterval = window.setInterval(() => {
      const timeSinceLastChange = Date.now() - this.lastChangeTime;
      // Only sync if changes were made and it's been at least 2 seconds since last change
      if (this.lastChangeTime > 0 && timeSinceLastChange > 6000) {
        this.performSync();
      }
    }, this.syncIntervalMs);
    
    //console.log(`[AutoSyncService] Started sync interval: ${this.syncIntervalMs}ms`);
  }
  
  /**
   * Stop the sync interval
   */
  public stopSyncInterval(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      //console.log('[AutoSyncService] Stopped sync interval');
    }
  }
  
  /**
   * Handle data change event
   */
  private handleDataChange(): void {
    //console.log('[AutoSyncService] Data change detected, triggering debounced sync');
    this.lastChangeTime = Date.now();
    this.debouncedSync();
  }


  private async performSync(): Promise<boolean> {
    // Don't sync if already syncing
    if (this.state.status === 'syncing') {
      //console.log('[AutoSyncService] Already syncing, skipping');
      return false;
    }
    
    // Set timeout to prevent stuck state
    const syncTimeout = setTimeout(() => {
      if (this.state.status === 'syncing') {
        console.error('[AutoSyncService] Sync timeout - resetting state');
        this.updateState({
          status: 'error',
          lastSyncTime: Date.now(),
          error: 'Sync operation timed out'
        });
      }
    }, 60000); // 60 second timeout
    
    // Update state to syncing
    this.updateState({
      status: 'syncing',
      lastSyncTime: this.state.lastSyncTime,
      error: null
    });
    
    try {
      // Check auth token
      //const token = localStorage.getItem('authToken');
      //if (!token) {
      const token = authService.getAuthToken();
      if (!token || !authService.hasValidToken()) {
        clearTimeout(syncTimeout);
        this.updateState({
          status: 'error',
          lastSyncTime: Date.now(),
          error: 'Authentication required'
        });
        return false;
      }
      
      // Get user data to sync
      const userData = this.dataSyncService.getAllData();
      console.log('[AutoSyncService] Data to sync:', {
        hasImages: !!userData.images,
        imageCount: userData.images?.messages?.length || 0,
        imagesData: userData.images
      });
      ////console.log('[AutoSyncService] User data for sync:', userData);
      
      // Check if there's data to sync
      if (!userData || Object.keys(userData).length === 0) {
        clearTimeout(syncTimeout);
        //console.log('[AutoSyncService] No data to sync');
        this.updateState({
          status: 'idle',
          lastSyncTime: Date.now(),
          error: null
        });
        return false;
      }
      
      //console.log('[AutoSyncService] Syncing data with server');
      
      // Transform data for API endpoints
      const transformedData = {
        // Only include non-conversation data types
        // Around line 295, replace the old locations transformation
        locations: userData.locations?.cards?.map(loc => ({
          name: loc.name,
          city: loc.city,
          country: loc.country,
          coordinates: [loc.position.lat, loc.position.lng],
          rating: loc.rating,
          reviews: loc.reviews,
          notes: loc.description || '',
          visited: false,
          // Add new fields
          type: loc.type,
          cuisine: loc.cuisine,
          priceLevel: loc.priceLevel,
          formattedAddress: loc.formattedAddress,
          businessStatus: loc.businessStatus,
          placeId: loc.placeId
        })) || [], 
        
        weather: userData.weather?.widgets?.map(widget => ({
          location: widget.location,
          temperature: widget.temperature,
          condition: widget.condition,
          forecast: widget.forecast
        })) || [],
        
        images: userData.images?.messages || [],
        
        preferences: userData.preferences || { uiMode: 'light', mapPreference: 'satellite' },
        
        mapState: userData.mapState ? {
          centerLat: userData.mapState.center.lat,
          centerLng: userData.mapState.center.lng,
          zoom: userData.mapState.zoom,
          visibleLocationIds: userData.mapState.visibleLocationIds,
          selectedLocationId: userData.mapState.selectedLocationId
        } : null,
      };
      
      // DELTA SYNC - Handle conversation separately using delta approach
      let conversationResult = { endpoint: 'conversation', success: true, skipped: true };
      
      if (userData.conversation?.sequence?.length > 0) {
        // Check for changed conversation elements
        const changedIds = this.dataSyncService.getChangedEntityIds('conversation');
        
        if (changedIds.size > 0) {
          //console.log(`[AutoSyncService] Delta sync: found ${changedIds.size} changed conversation elements`);
          
          // Extract only changed elements
          const changedElements = userData.conversation.sequence.filter(
            element => changedIds.has(element.elementId)
          );
          
          if (changedElements.length > 0) {
            try {
              // Use delta endpoint
              const response = await fetch(`/api/user-data/conversation/delta`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ elements: changedElements })
              });
              
              conversationResult = {
                endpoint: 'conversation',
                success: response.ok,
                status: response.status,
                skipped: false
              };
              
              if (response.ok) {
                // Clear tracked changes after successful sync
                this.dataSyncService.clearChangedEntityIds('conversation', changedIds);
                //console.log(`[AutoSyncService] Delta sync successful for ${changedElements.length} elements`);
              }
            } catch (err) {
              console.error(`[AutoSyncService] Error in delta sync:`, err);
              conversationResult = { 
                endpoint: 'conversation', 
                success: false, 
                error: err instanceof Error ? err.message : String(err), 
                skipped: false 
              };
            }
          }
        } else {
          //console.log('[AutoSyncService] No changed conversation elements to sync');
        }
      }

      // In existing performSync method, after handling conversation delta
      // Handle locations delta sync
      let locationsResult = { endpoint: 'locations', success: true, skipped: true };

      if (userData.locations?.cards?.length > 0) {
        
        // Check for changed location cards
        const changedLocationIds = this.dataSyncService.getChangedEntityIds('locations');

        console.log(`[AutoSyncService] Delta sync: found ${changedLocationIds.size} changed location IDs:`, Array.from(changedLocationIds));
        
        if (changedLocationIds.size > 0) {

            console.log('[SAVE PATH] Using DELTA SYNC for locations:', changedLocationIds.size);

          //console.log(`[AutoSyncService] Delta sync: found ${changedLocationIds.size} changed locations`);
          
          // Extract only changed locations
          const changedLocations = userData.locations.cards.filter(
            card => changedLocationIds.has(card.id)
          );
          
          if (changedLocations.length > 0) {
            try {
              // Use delta endpoint
              const response = await fetch(`/api/user-data/locations/delta`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ locations: changedLocations })
              });
              
              locationsResult = {
                endpoint: 'locations',
                success: response.ok,
                status: response.status,
                skipped: false
              };
              
              if (response.ok) {
                // Clear tracked changes after successful sync
                console.log('[AutoSyncService] Before clearing:', this.dataSyncService.getChangedEntityIds('locations').size);
                this.dataSyncService.clearChangedEntityIds('locations', changedLocationIds);
                console.log('[AutoSyncService] After clearing:', this.dataSyncService.getChangedEntityIds('locations').size);
                //console.log(`[AutoSyncService] Delta sync successful for ${changedLocations.length} locations`);
              }
            } catch (err) {
              console.error(`[AutoSyncService] Error in locations delta sync:`, err);
              locationsResult = { 
                endpoint: 'locations', 
                success: false, 
                error: err instanceof Error ? err.message : String(err), 
                skipped: false 
              };
            }
          }
        }
      }

      // Images delta sync
      // Images delta sync - FIXED VERSION
      let imagesResult = { endpoint: 'images', success: true, skipped: true };
      if (userData.images?.messages?.length > 0) {
        const changedImageIds = this.dataSyncService.getChangedEntityIds('images');
        //console.log(`[AutoSyncService] Image delta sync check: ${changedImageIds.size} changed IDs`);
        //console.log('[AutoSyncService] Changed image IDs:', Array.from(changedImageIds));
        
        if (changedImageIds.size > 0) {
          const changedImages = userData.images.messages.filter(img => changedImageIds.has(img.id));
          //console.log(`[AutoSyncService] Found ${changedImages.length} changed images to sync`);
          console.log('[AutoSyncService] Changed images:', changedImages.map(img => ({
            id: img.id,
            hasImageUrl: !!img.imageUrl,
            imageUrlLength: img.imageUrl?.length || 0
          })));
          
          if (changedImages.length > 0) {
            try {
              const url = `/api/user-data/images/delta`;
              const payload = { images: changedImages };
              
              //console.log(`[AutoSyncService] Calling ${url} with ${changedImages.length} images`);
              console.log('[AutoSyncService] Request headers:', {
                'Authorization': `Bearer ${token?.substring(0, 20)}...`,
                'Content-Type': 'application/json'
              });
              
              const response = await fetch(url, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
              });
              
              //console.log(`[AutoSyncService] Images delta response status: ${response.status}`);
              
              const responseText = await response.text();
              //console.log('[AutoSyncService] Images delta response:', responseText);
              
              let responseData;
              try {
                responseData = JSON.parse(responseText);
              } catch (e) {
                console.error('[AutoSyncService] Failed to parse response as JSON:', e);
                responseData = null;
              }
              
              imagesResult = {
                endpoint: 'images',
                success: response.ok,
                status: response.status,
                skipped: false,
                data: responseData
              };
              
              if (response.ok) {
                this.dataSyncService.clearChangedEntityIds('images', changedImageIds);
                //console.log(`[AutoSyncService] Images delta sync successful, cleared ${changedImageIds.size} change tracking IDs`);
              } else {
                console.error(`[AutoSyncService] Images delta sync failed with status ${response.status}`);
                console.error('[AutoSyncService] Response body:', responseText);
              }
            } catch (err) {
              console.error(`[AutoSyncService] Error in images delta sync:`, err);
              console.error('[AutoSyncService] Error details:', {
                message: err.message,
                stack: err.stack,
                name: err.name
              });
              
              imagesResult = { 
                endpoint: 'images', 
                success: false, 
                error: err instanceof Error ? err.message : String(err), 
                skipped: false 
              };
            }
          } else {
            //console.log('[AutoSyncService] No valid changed images found after filtering');
          }
        } else {
          //console.log('[AutoSyncService] No changed image IDs tracked');
        }
      } else {
        //console.log('[AutoSyncService] No images in userData to sync');
      }



      // Weather delta sync
      let weatherResult = { endpoint: 'weather', success: true, skipped: true };
      console.log('[AutoSyncService] Weather data at sync time: --------', userData.weather?.widgets?.length || 0);
      if (userData.weather?.widgets?.length > 0) {
        const changedWeatherIds = this.dataSyncService.getChangedEntityIds('weather');
        if (changedWeatherIds.size > 0) {
          //console.log(`[AutoSyncService] Delta sync: found ${changedWeatherIds.size} changed weather widgets`);
          const changedWidgets = userData.weather.widgets.filter(
            widget => changedWeatherIds.has(widget.id)
          );
          
          if (changedWidgets.length > 0) {
            try {
              const response = await fetch(`/api/user-data/weather/delta`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ widgets: changedWidgets })
              });
              
              weatherResult = {
                endpoint: 'weather',
                success: response.ok,
                status: response.status,
                skipped: false
              };
              
              if (response.ok) {
                this.dataSyncService.clearChangedEntityIds('weather', changedWeatherIds);
                //console.log(`[AutoSyncService] Delta sync successful for ${changedWidgets.length} weather widgets`);
              }
            } catch (err) {
              console.error(`[AutoSyncService] Error in weather delta sync:`, err);
              weatherResult = { 
                endpoint: 'weather', 
                success: false, 
                error: err instanceof Error ? err.message : String(err), 
                skipped: false 
              };
            }
          }
        }
      }

      // Preferences delta sync
      let preferencesResult = { endpoint: 'preferences', success: true, skipped: true };
      if (userData.preferences) {
        const changedPrefs = this.dataSyncService.getChangedEntityIds('preferences');
        if (changedPrefs.size > 0) {
          //console.log('[AutoSyncService] Delta sync: preferences have changed');
          try {
            const response = await fetch(`/api/user-data/preferences/delta`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(userData.preferences)
            });
            
            preferencesResult = {
              endpoint: 'preferences',
              success: response.ok,
              status: response.status,
              skipped: false
            };
            
            if (response.ok) {
              this.dataSyncService.clearChangedEntityIds('preferences', changedPrefs);
              //console.log('[AutoSyncService] Delta sync successful for preferences');
            }
          } catch (err) {
            console.error(`[AutoSyncService] Error in preferences delta sync:`, err);
            preferencesResult = { 
              endpoint: 'preferences', 
              success: false, 
              error: err instanceof Error ? err.message : String(err), 
              skipped: false 
            };
          }
        }
      }

      // Update Promise.all to include new results and exclude these items from standard sync
      const results = await Promise.all([
        Promise.resolve(conversationResult),
        Promise.resolve(locationsResult),
        Promise.resolve(imagesResult),
        Promise.resolve(weatherResult),
        Promise.resolve(preferencesResult),

        // Process other endpoints - EXCLUDING locations since we handled it with delta
        ...Object.entries(transformedData)
        .filter(([endpoint]) => !['locations', 'images', 'weather', 'preferences'].includes(endpoint))
        .map(async ([endpoint, data]) => {
          if (!data || (Array.isArray(data) ? data.length === 0 : Object.keys(data).length === 0)) {
            return { endpoint, success: true, skipped: true };
          }
          
          try {
            //console.log(`[AutoSyncService] Syncing ${endpoint} data:`, data);
            const response = await fetch(`/api/user-data/${endpoint}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(data)
            });
            
            const result = {
              endpoint,
              success: response.ok,
              status: response.status,
              skipped: false
            };
            
            //console.log(`[AutoSyncService] ${endpoint} sync ${result.success ? 'succeeded' : 'failed'} with status ${result.status}`);
            return result;
          } catch (err) {
            console.error(`[AutoSyncService] Error syncing ${endpoint}:`, err);
            return { 
              endpoint, 
              success: false, 
              error: err instanceof Error ? err.message : String(err), 
              skipped: false 
            };
          }
        })
      ]);
      
      clearTimeout(syncTimeout);
      
      // Check if any sync failed
      const failedEndpoints = results.filter(r => !r.success && !r.skipped);
      
      if (failedEndpoints.length > 0) {
        const errorMsg = `Failed to sync: ${failedEndpoints.map(f => f.endpoint).join(', ')}`;
        console.error('[AutoSyncService] Sync errors:', errorMsg);
        this.updateState({
          status: 'error',
          lastSyncTime: Date.now(),
          error: errorMsg
        });
        return false;
      }
      
      // All syncs successful
      //console.log('[AutoSyncService] All data synced successfully');
      this.updateState({
        status: 'success',
        lastSyncTime: Date.now(),
        error: null
      });
      
      // Reset last change time
      this.lastChangeTime = 0;
      
      return true;
    } catch (error) {
      clearTimeout(syncTimeout);
      console.error('[AutoSyncService] Sync error:', error);
      
      this.updateState({
        status: 'error',
        lastSyncTime: Date.now(),
        error: error instanceof Error ? error.message : String(error)
      });
      
      return false;
    }
  }  
  
  /**
   * Manually trigger sync
   */
  public async sync(): Promise<boolean> {
    return this.performSync();
  }
  
  /**
   * Update sync state and notify listeners
   */
  private updateState(newState: SyncState): void {
    this.state = newState;
    
    // Dispatch event for UI components
    window.dispatchEvent(new CustomEvent('tripper-sync-status-updated', {
      detail: this.state
    }));
    
    // Notify direct listeners
    this.syncListeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('[AutoSyncService] Error in sync listener:', error);
      }
    });
  }
  
  /**
   * Get current sync state
   */
  public getSyncState(): SyncState {
    return this.state;
  }
  
  /**
   * Add sync state listener
   */
  public addSyncListener(listener: (state: SyncState) => void): void {
    this.syncListeners.push(listener);
  }
  
  /**
   * Remove sync state listener
   */
  public removeSyncListener(listener: (state: SyncState) => void): void {
    this.syncListeners = this.syncListeners.filter(l => l !== listener);
  }
  
  /**
   * Check if network is available
   */
  public isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Set a custom sync function to override the default sync behavior
   * Add this method to the AutoSyncService class
   */
  public setCustomSyncFunction(syncFunction: () => Promise<boolean>): void {
    this.performSync = syncFunction;
    //console.log('[AutoSyncService] Custom sync function set');
  }

  /**
   * Dispose of service
   */
  public dispose(): void {
    this.stopSyncInterval();
    window.removeEventListener('tripper-data-updated', this.handleDataChange.bind(this));
    this.syncListeners = [];
  }
}
